import { useEffect, useRef, useState } from "react";
import { Image } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { UploadService } from "@/services/APIs";
import CustomButton from "../common/Button";
import { Trash } from "lucide-react";
import { useParams } from "react-router-dom";

type CustomImageUploadProps = {
  folderName?: string;
  onUploaded?: (response: any, file: File) => void;
  boxWidth?: number;
  boxHeight?: number;
  disabled?: boolean;
  accept?: string;
  shape?: "square" | "circle";
  borderStyle?: "solid" | "dashed" | "dotted" | "double" | "hidden";
  value?:
    | string
    | { id: string; url: string; name: string; size: number }
    | null;
  onChange?: (
    value:
      | string
      | { id: string; url: string; name: string; size: number }
      | null
  ) => void;
  initialUrl?: string;
  className?: string;
};

export const CustomImageUpload: React.FC<CustomImageUploadProps> = ({
  folderName = "images",
  onUploaded,
  boxWidth,
  boxHeight,
  disabled = false,
  accept = "image/*",
  borderStyle = "dashed",
  className,
  value,
  onChange,
  initialUrl,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const DEFAULT_SIZE = 160;
  const W = boxWidth ?? DEFAULT_SIZE;
  const H = boxHeight ?? DEFAULT_SIZE;

  const [previewUrl, setPreviewUrl] = useState<string | null>(
    initialUrl ?? null
  );
  const [isObjectPreview, setIsObjectPreview] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const { id } = useParams();
  const isEditMode = !!id;

  // Sync controlled value
  useEffect(() => {
    const fetchFile = async () => {
      if (typeof value !== "undefined" && isEditMode && value !== null) {
        const url = typeof value === "string" ? value : (value?.url ?? null);
        const response = await UploadService.getFileById({ id: url as string });
        console.log("response", response);
        setPreviewUrl(response?.file_path_optimized);
        setIsObjectPreview(false);
      }
    };
    fetchFile();
  }, [value, isEditMode]);

  // Cleanup object URLs
  useEffect(() => {
    return () => {
      if (isObjectPreview && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [isObjectPreview, previewUrl]);

  const openPicker = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const extractUrl = (res: any): string | null => {
    return (
      res?.[0]?.file_path ??
      res?.data?.[0]?.url ??
      res?.files?.[0]?.url ??
      res?.url ??
      null
    );
  };

  const extractId = (res: any): string | null => {
    return (
      res?.[0]?.id ??
      res?.data?.[0]?.id ??
      res?.files?.[0]?.id ??
      res?.id ??
      null
    );
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      e.target.value = "";
      return;
    }

    // Show temp preview
    if (isObjectPreview && previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    const tempUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(tempUrl);
    setIsObjectPreview(true);

    try {
      const response = await UploadService.uploadFiles({
        folderName,
        files: [file],
      });

      const finalUrl = extractUrl(response);
      const fileId = extractId(response);

      if (finalUrl) {
        if (isObjectPreview && previewUrl) {
          URL.revokeObjectURL(previewUrl); // Revoke the temporary URL
        }
        setPreviewUrl(finalUrl); // Use the file_path from the response
        setIsObjectPreview(false); // Mark as not a temporary object URL

        // ✅ Send correct value to Formily
        if (fileId) {
          onChange?.(fileId);
        } else {
          onChange?.(null);
        }

        onUploaded?.(response, file);
      } else {
        console.error("No valid URL extracted from response:", response);
        // Keep tempUrl as fallback if finalUrl is not available
      }
    } catch (error) {
      console.error("Upload failed:", error);
      // Keep tempUrl as fallback on error
    } finally {
      e.target.value = ""; // Clear input
    }
  };

  const clearSelection = () => {
    if (isObjectPreview && previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsObjectPreview(false);
    onChange?.(null); // ✅ Only send value
  };

  const borderStyleClass =
    borderStyle === "dashed"
      ? "border-dashed"
      : borderStyle === "dotted"
        ? "border-dotted"
        : borderStyle === "double"
          ? "border-double"
          : borderStyle === "hidden"
            ? "border-none"
            : "";

  const borderWidthClass = borderStyle === "hidden" ? "border-0" : "border-2";
  const borderColorClass =
    borderStyle === "hidden" ? "" : "border-gray-300 hover:border-gray-400";

  return (
    <div className={`space-y-2 ${className}`}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {!previewUrl ? (
        <div
          role="button"
          tabIndex={0}
          onClick={openPicker}
          onKeyDown={e => (e.key === "Enter" || e.key === " ") && openPicker()}
          className={[
            "flex cursor-pointer items-center justify-center rounded-md text-gray-400 transition hover:text-gray-600",
            borderWidthClass,
            borderStyleClass,
            borderColorClass,
          ].join(" ")}
          style={{ width: W, height: H }}
          aria-label="Select image"
        >
          <PlusOutlined style={{ fontSize: 28 }} />
        </div>
      ) : (
        <div
          className="relative cursor-pointer"
          style={{ width: W, height: H }}
          role="button"
          tabIndex={0}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onKeyDown={e => (e.key === "Enter" || e.key === " ") && openPicker()}
          aria-label="Change image"
        >
          <Image
            src={previewUrl}
            alt={selectedFile?.name ?? "image"}
            preview
            width={W}
            height={H}
            className={[
              "flex cursor-pointer items-center justify-center rounded-md text-gray-400",
              borderWidthClass,
              borderStyleClass,
              borderColorClass,
            ].join(" ")}
            style={{ objectFit: "cover", borderRadius: 8 }}
          />
          {isHovered && (
            <CustomButton
              danger
              key="delete"
              icon={<Trash size={16} />}
              type="text"
              title=""
              onClick={clearSelection}
              className="absolute right-1 top-1 rounded-full p-2 text-gray-900 shadow dark:text-gray-100"
            />
          )}
        </div>
      )}
    </div>
  );
};
